import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from '../../../lib/api.service';
import { LogService } from '../../../lib/log.service';
import { ModalService } from '../../../lib/modal.service';
import { Organization } from '../../../models/organization';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';

@Component({
    selector: 'counselor-user-associations',
    templateUrl: './counselor-user-associations.html',
    styleUrls: ['./counselor-user-associations.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CounselorUserAssociations implements OnInit, OnDestroy {
  isloaded = false;
  isFiltering = false;
  associations: any[] = [];
  organizations: Organization[] = [];
  userEmail: string = '';
  counselorEmail: string = '';
  confirmedFilter: string = '';
  organizationIds: any = null;

  /**
   * Text filters are debounced so typing fires one request instead of one per
   * keystroke; switchMap drops superseded responses so a slow early request
   * cannot overwrite the results of a later one.
   */
  private filter$ = new Subject<void>();
  private filterSub: Subscription;

  constructor(
    private api: ApiService,
    private log: LogService,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    this.api.get('admin/organizations').subscribe(
      (results: any) => {
        this.organizations = Organization.initializeArray(results.data);
        this.isloaded = true;
      },
      (error: any) => {
        this.log.error('Error loading. ' + error.message);
        this.isloaded = true;
      }
    );

    this.filterSub = this.filter$
      .pipe(
        debounceTime(400),
        switchMap(() => {
          this.isFiltering = true;
          // Errors are swallowed inside switchMap so a single failed request
          // does not tear down the filter stream for the rest of the session.
          return this.api.get('admin/counselorpairings', this.filterParams()).pipe(
            catchError((error: any) => {
              this.log.error('Error loading. ' + error.message);
              return of(null);
            })
          );
        })
      )
      .subscribe((results: any) => {
        if (results) {
          this.associations = results.data;
        }
        this.isFiltering = false;
      });

    this.loadListing();
  }

  ngOnDestroy() {
    if (this.filterSub) {
      this.filterSub.unsubscribe();
    }
  }

  /** Debounced — for the free-text filters. */
  onFilterChange() {
    this.filter$.next();
  }

  /** Immediate — dropdowns produce one discrete change, nothing to debounce. */
  onSelectFilterChange() {
    this.loadListing();
  }

  loadListing() {
    this.isFiltering = true;
    this.api.get('admin/counselorpairings', this.filterParams()).subscribe(
      (results: any) => {
        this.associations = results.data;
        this.isFiltering = false;
      },
      (error: any) => {
        this.log.error('Error loading. ' + error.message);
        this.isFiltering = false;
      }
    );
  }

  private filterParams() {
    const params: any = {
      userEmail: this.userEmail,
      counselorEmail: this.counselorEmail,
    };
    if (this.confirmedFilter !== '') {
      params.confirmed = this.confirmedFilter === 'true';
    }
    if (this.organizationIds) {
      params.organizationIds = this.organizationIds;
    }
    return params;
  }

  onApprove(id: number) {
    this.api.post(`counselors/approveassociation/${id}`, {}).subscribe(
      (result: any) => {
        this.modalService.showAlert('Success', 'Successfully approved the association!');
        this.loadListing();
      },
      (error: any) => {
        this.modalService.showAlert('Error', 'Something went wrong!');
      }
    );
  }

  onRemove(id: number) {
    this.modalService
      .showConfirmation('Delete', 'Are you sure you want to remove this association?')
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.api.post(`counselors/removeassociation/${id}`, {}).subscribe(
            (result: any) => {
              this.modalService.showAlert('Success', 'Successfully removed the association!');
              this.loadListing();
            },
            (error: any) => {
              this.modalService.showAlert('Error', 'Something went wrong!');
            }
          );
        }
      });
  }
}
